import { useEffect, useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, ArrowRight, Gift } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { PLAN_META } from "@/lib/planFeatures";
import { RedeemCodeInline } from "@/components/RedeemCodeInline";
import api from "@/lib/api";
import { readPendingPlan, clearPendingPlan, describePendingPlan } from "@/lib/pendingPlan";
import { linesByPlan } from "@/lib/planCatalog";

const TIERS = [
  {
    key: "starter", name: "Starter", monthly: 0, yearly: 0,
    tagline: "Everything you need to start reporting.",
    cta: "Continue with Starter (free)",
    benefits: ["Up to 25 investors", "2 AI-drafted updates / month", "Investor portal", "Company-health dashboard"],
  },
  { ...PLAN_META.growth, highlight: true, cta: "Choose Growth" },
  { ...PLAN_META.scale, cta: "Choose Scale" },
  { ...PLAN_META.fund_manager, cta: "Choose Fund Manager" },
];

export const describeCoupon = (c) =>
  `${c.code}: ${c.percent_off ? `${c.percent_off}% off` : `$${c.amount_off} off`}` +
  (c.duration === "repeating" ? ` for ${c.duration_in_months} months` : c.duration === "forever" ? " forever" : "");

export default function ChoosePlan() {
  const { user, company } = useAuth();
  const navigate = useNavigate();
  const [pending] = useState(readPendingPlan);
  const [annual, setAnnual] = useState(pending ? pending.interval === "yearly" : true);
  const [loadingKey, setLoadingKey] = useState(null);
  const [myCoupon, setMyCoupon] = useState(null);
  const [catalog, setCatalog] = useState([]);
  useEffect(() => {
    api.get("/payments/my-coupon").then(({ data }) => data.has_coupon && setMyCoupon(data)).catch(() => {});
    api.get("/plans").then(({ data }) => setCatalog(data.plans || [])).catch(() => {});
  }, []);
  sessionStorage.removeItem("tenx_choose_plan");

  const isTeamMember = company?.founder_user_id && user && company.founder_user_id !== user.id;
  const alreadyPaid = !!company?.plan && company.plan !== "starter";
  const isGP = company?.org_type === "gp";
  const byKey = Object.fromEntries(catalog.map((p) => [p.key, p]));
  const catalogLines = catalog.length ? linesByPlan(catalog) : {};
  const tiers = TIERS.map((t) => ({
    ...t,
    monthly: byKey[t.key]?.price_monthly ?? t.monthly,
    yearly: byKey[t.key]?.price_yearly ?? t.yearly,
    benefits: catalogLines[t.key]?.length ? catalogLines[t.key] : t.benefits,
    highlight: pending ? t.key === pending.plan : isGP ? t.key === "fund_manager" : t.highlight,
    cta: pending && t.key === pending.plan ? `Continue with ${t.name}` : t.cta,
  }));
  useEffect(() => {
    if (isTeamMember) toast.info("Only the workspace owner can choose a plan. Taking you to your dashboard.");
    else if (alreadyPaid) toast.info(`You're already on the ${PLAN_META[company.plan]?.name || company.plan} plan. Manage or change it from Billing.`);
  }, [isTeamMember, alreadyPaid, company?.plan]);
  if (isTeamMember) return <Navigate to="/app" replace />;
  if (alreadyPaid) return <Navigate to="/app/billing" replace />;

  const pick = async (key) => {
    clearPendingPlan();
    if (key === "starter") { navigate("/app"); return; }
    setLoadingKey(key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: `${key}_${annual ? "yearly" : "monthly"}`,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setLoadingKey(null);
    }
  };

  return (
    <div className="flex min-h-screen flex-col justify-center bg-background px-5 py-10" data-testid="choose-plan-page">
      <div className="mx-auto max-w-5xl">
        <div className="flex justify-center"><Logo /></div>
        <h1 className="ff-cabinet mt-8 text-center text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          Pick the plan for {company?.name || "your company"}
        </h1>
        <p className="ff-manrope mt-2 text-center text-slate-600">
          Start free and upgrade anytime. You can change plans later from Billing.
        </p>

        {pending && (
          <div className="mx-auto mt-4 flex max-w-lg items-center justify-center gap-2 rounded-full border border-brand/40 bg-brand/10 px-4 py-2 text-sm font-medium text-foreground" data-testid="choose-plan-preselected">
            <Check className="h-4 w-4 shrink-0 text-brand-ink" />
            {describePendingPlan(pending)} is selected from Pricing. Confirm below or pick another plan.
          </div>
        )}
        {myCoupon && (
          <div className="mx-auto mt-4 flex max-w-lg items-center justify-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-800" data-testid="choose-plan-coupon-banner">
            <Gift className="h-4 w-4 shrink-0" />
            {describeCoupon(myCoupon)}, applied automatically at checkout
          </div>
        )}

        <div className="mt-6 flex justify-center">
          <div className="inline-flex items-center rounded-full border border-slate-200 bg-white p-1" data-testid="choose-plan-billing-toggle">
            <button onClick={() => setAnnual(false)} className={`ff-manrope rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${!annual ? "bg-slate-900 text-white" : "text-slate-600"}`} data-testid="choose-plan-toggle-monthly">Monthly</button>
            <button onClick={() => setAnnual(true)} className={`ff-manrope rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${annual ? "bg-slate-900 text-white" : "text-slate-600"}`} data-testid="choose-plan-toggle-annual">Annual <span className={annual ? "text-brand" : "text-brand-ink"}>-17%</span></button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {tiers.map((t) => {
            const price = t.monthly === 0 ? 0 : annual ? Math.round(t.yearly / 12) : t.monthly;
            return (
              <div key={t.key} data-testid={`choose-plan-card-${t.key}`} data-preselected={pending?.plan === t.key ? "true" : undefined}
                className={`flex flex-col rounded-xl border p-6 ${t.highlight ? "border-slate-900 bg-slate-900 text-white shadow-xl" : "border-slate-200 bg-white"} ${pending?.plan === t.key ? "ring-2 ring-brand ring-offset-2" : ""}`}>
                {t.highlight && <span data-testid={`choose-plan-popular-${t.key}`} className="mb-3 self-start rounded-full bg-brand px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand-foreground">Most popular</span>}
                <h3 className={`ff-cabinet text-xl font-bold tracking-tight ${t.highlight ? "text-white" : "text-slate-900"}`}>{t.name}</h3>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className={`ff-cabinet text-4xl font-black tracking-tighter ${t.highlight ? "text-white" : "text-slate-900"}`}>${price}</span>
                  <span className={`ff-manrope text-sm font-medium ${t.highlight ? "text-slate-300" : "text-slate-500"}`}>/mo</span>
                </div>
                <p className={`ff-manrope mt-1 h-4 text-xs font-medium ${t.highlight ? "text-slate-400" : "text-slate-400"}`}>
                  {t.monthly > 0 ? (annual ? "billed annually" : "billed monthly") : "free forever"}
                </p>
                <Button onClick={() => pick(t.key)} disabled={loadingKey === t.key} data-testid={`choose-plan-cta-${t.key}`}
                  className={`ff-manrope mt-5 w-full gap-1.5 rounded-md font-semibold ${t.highlight ? "bg-white text-slate-900 hover:bg-slate-100" : "bg-slate-900 text-white hover:bg-slate-800"}`}>
                  {loadingKey === t.key ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {t.cta}
                </Button>
                <ul className="mt-6 space-y-2.5">
                  {t.benefits.map((f) => (
                    <li key={f} className={`ff-manrope flex items-start gap-2 text-sm font-medium ${t.highlight ? "text-slate-200" : "text-slate-600"}`}>
                        <Check className={`mt-0.5 h-4 w-4 shrink-0 ${t.highlight ? "text-brand" : "text-brand-ink"}`} strokeWidth={2.2} /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="mx-auto mt-8 max-w-md rounded-xl border border-slate-200 bg-white p-5 text-center" data-testid="choose-plan-redeem">
          <p className="ff-manrope text-sm font-semibold text-slate-700">Have an access code?</p>
          <p className="ff-manrope mb-3 text-xs text-slate-500">Redeem it to unlock a paid plan for free.</p>
          <div className="flex justify-center"><RedeemCodeInline onDone={() => navigate("/app")} /></div>
        </div>

        <div className="mt-8 flex flex-col items-center gap-2">
          <button onClick={() => { clearPendingPlan(); navigate("/app"); }} className="ff-manrope inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800" data-testid="choose-plan-skip">
            Skip for now, continue with the free Starter plan <ArrowRight className="h-3.5 w-3.5" />
          </button>
          <p className="ff-manrope text-xs text-slate-500">
            Need unlimited investors, SSO or an SLA? <Link to="/contact" className="font-semibold text-slate-600 hover:underline" data-testid="choose-plan-enterprise-link">Talk to us about Enterprise</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
